// src/pages/Notifications.jsx
import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import {
  collection, query, orderBy, limit, onSnapshot, doc, updateDoc,
  deleteDoc, arrayRemove, arrayUnion, writeBatch, startAfter, getDocs,
  setDoc,
} from 'firebase/firestore';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const PAGE_SIZE = 30;
const UNDO_WINDOW_MS = 5000;

const normalizeRole = (r) => String(r || '').trim().toLowerCase();

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const toMillis = (value) => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
  }
  return null;
};

const formatRelative = (value) => {
  const ts = toMillis(value);
  if (!ts) return '';
  const diffMs = Date.now() - ts;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return new Date(ts).toLocaleDateString();
};

const dateGroupLabel = (value) => {
  const ts = toMillis(value);
  if (!ts) return 'Earlier';
  const now = new Date();
  const d = new Date(ts);
  if (now.toDateString() === d.toDateString()) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (y.toDateString() === d.toDateString()) return 'Yesterday';
  if (now - d < 7 * 24 * 60 * 60 * 1000) return 'This week';
  return 'Earlier';
};

const getTypeStyle = (type) => {
  switch (type) {
    case 'post_approved':
    case 'story_approved':
    case 'user_approved':
      return {
        icon: 'fa-circle-check',
        color: 'text-green-600',
        bg: 'bg-green-100 dark:bg-green-900/30',
      };
    case 'post_rejected':
    case 'story_rejected':
    case 'user_rejected':
      return {
        icon: 'fa-circle-xmark',
        color: 'text-red-600',
        bg: 'bg-red-100 dark:bg-red-900/30',
      };
    case 'award_added':
    case 'award_updated':
      return {
        icon: 'fa-trophy',
        color: 'text-yellow-600',
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      };
    case 'announcement':
      return {
        icon: 'fa-bullhorn',
        color: 'text-orange',
        bg: 'bg-orange/10',
      };
    default:
      return {
        icon: 'fa-bell',
        color: 'text-orange',
        bg: 'bg-orange/10',
      };
  }
};

/* ================================================================== */
/* Confirm Dialog                                                      */
/* ================================================================== */

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open || busy) return undefined;
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => cancelRef.current?.focus(), 80);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <motion.div
        initial={{ scale: 0.95, y: 8 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 8 }}
        transition={{ type: 'spring', stiffness: 340, damping: 26 }}
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <i
              className="fas fa-triangle-exclamation text-red-500"
              aria-hidden="true"
            />
          </div>
          <div className="flex-1">
            <h3
              id="confirm-title"
              className="text-lg font-bold text-gray-800 dark:text-white mb-1"
            >
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {message}
            </p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-orange"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-red-300"
          >
            {busy && (
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            )}
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ================================================================== */
/* Undo toast                                                          */
/* ================================================================== */

const UndoToast = ({ count, onUndo, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, UNDO_WINDOW_MS);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] bg-gray-900 text-white px-4 py-3 rounded-xl shadow-xl flex items-center gap-3"
    >
      <i className="fas fa-trash-alt text-sm" aria-hidden="true" />
      <span className="text-sm">
        {count} notification{count === 1 ? '' : 's'} deleted
      </span>
      <button
        type="button"
        onClick={onUndo}
        className="text-orange font-medium text-sm hover:underline"
      >
        Undo
      </button>
    </motion.div>
  );
};

/* ================================================================== */
/* Skeleton                                                            */
/* ================================================================== */

const NotificationsSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div
        key={i}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-gray-200 dark:border-gray-700 animate-pulse"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-3 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-2 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ================================================================== */
/* Notifications Page                                                  */
/* ================================================================== */

const Notifications = () => {
  const { user, userData, isAdmin, isBoard, isMember } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = ['all', 'unread', 'read'].includes(
    searchParams.get('tab'),
  )
    ? searchParams.get('tab')
    : 'all';

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(initialTab);
  const [groupByDate, setGroupByDate] = useState(false);

  /* Selection state */
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [undoData, setUndoData] = useState(null);

  const markingInFlightRef = useRef(new Set());
  const mountedRef = useRef(true);
  const listRef = useRef(null);

  /* ---------------- Effective role ---------------- */
  const effectiveRole = useMemo(() => {
    if (isAdmin) return 'admin';
    if (isBoard) return 'board';
    if (isMember) return 'member';
    return normalizeRole(userData?.role) || 'member';
  }, [isAdmin, isBoard, isMember, userData?.role]);

  /* ---------------- URL sync ---------------- */
  useEffect(() => {
    const next = new URLSearchParams();
    if (tab !== 'all') next.set('tab', tab);
    setSearchParams(next, { replace: true });
  }, [tab, setSearchParams]);

  /* ---------------- Mount guard ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* ---------------- Live subscription ---------------- */
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return undefined;
    }

    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!mountedRef.current) return;
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const filtered = all.filter((n) => {
          if (n.recipientType === 'all') return true;
          if (n.recipientType === 'roles') {
            const roles = Array.isArray(n.recipients) ? n.recipients : [];
            return roles.some((r) => normalizeRole(r) === effectiveRole);
          }
          if (n.recipientType === 'users') {
            return (
              Array.isArray(n.recipients) && n.recipients.includes(user.uid)
            );
          }
          return false;
        });

        setNotifications(filtered);
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.size === PAGE_SIZE);
        setLoading(false);
      },
      (err) => {
        console.error('Notifications listener:', err);
        if (mountedRef.current) {
          setError('Failed to load notifications.');
          setLoading(false);
        }
      },
    );

    return () => unsub();
  }, [user?.uid, effectiveRole]);

  /* ---------------- Prune selections that no longer exist ---------------- */
  useEffect(() => {
    if (!selectionMode) return;
    setSelectedIds((prev) => {
      const alive = new Set(notifications.map((n) => n.id));
      const next = new Set();
      prev.forEach((id) => {
        if (alive.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [notifications, selectionMode]);

  /* ---------------- Filter by tab ---------------- */
  const displayed = useMemo(() => {
    if (!user?.uid) return notifications;
    if (tab === 'unread') {
      return notifications.filter((n) => !n.readBy?.includes(user.uid));
    }
    if (tab === 'read') {
      return notifications.filter((n) => n.readBy?.includes(user.uid));
    }
    return notifications;
  }, [notifications, tab, user?.uid]);

  /* ---------------- Grouped ---------------- */
  const grouped = useMemo(() => {
    if (!groupByDate) return null;
    const map = new Map();
    const order = ['Today', 'Yesterday', 'This week', 'Earlier'];
    displayed.forEach((n) => {
      const label = dateGroupLabel(n.createdAt);
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(n);
    });
    return order
      .filter((k) => map.has(k))
      .map((k) => ({ label: k, items: map.get(k) }));
  }, [displayed, groupByDate]);

  /* ---------------- Load more ---------------- */
  const loadMore = useCallback(async () => {
    if (!user?.uid || !lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'notifications'),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(PAGE_SIZE),
      );
      const snap = await getDocs(q);
      if (!mountedRef.current) return;

      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = all.filter((n) => {
        if (n.recipientType === 'all') return true;
        if (n.recipientType === 'roles') {
          const roles = Array.isArray(n.recipients) ? n.recipients : [];
          return roles.some((r) => normalizeRole(r) === effectiveRole);
        }
        if (n.recipientType === 'users') {
          return (
            Array.isArray(n.recipients) && n.recipients.includes(user.uid)
          );
        }
        return false;
      });

      setNotifications((prev) => [...prev, ...filtered]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.size === PAGE_SIZE);
    } catch (err) {
      console.error('Load more failed:', err);
      if (mountedRef.current) {
        setError('Failed to load more notifications.');
      }
    } finally {
      if (mountedRef.current) setLoadingMore(false);
    }
  }, [user?.uid, lastDoc, loadingMore, effectiveRole]);

  /* ---------------- Mark single as read ---------------- */
  const markAsRead = useCallback(
    async (notifId) => {
      if (!user?.uid) return;
      if (markingInFlightRef.current.has(notifId)) return;
      markingInFlightRef.current.add(notifId);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notifId && !n.readBy?.includes(user.uid)
            ? { ...n, readBy: [...(n.readBy || []), user.uid] }
            : n,
        ),
      );

      try {
        await updateDoc(doc(db, 'notifications', notifId), {
          readBy: arrayUnion(user.uid),
        });
      } catch (err) {
        console.error('Mark as read failed:', err);
        if (mountedRef.current) {
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notifId
                ? {
                    ...n,
                    readBy: (n.readBy || []).filter((u) => u !== user.uid),
                  }
                : n,
            ),
          );
          setError('Failed to mark as read.');
        }
      } finally {
        markingInFlightRef.current.delete(notifId);
      }
    },
    [user?.uid],
  );

  /* ---------------- Mark all as read ---------------- */
  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;
    const unread = notifications.filter((n) => !n.readBy?.includes(user.uid));
    if (unread.length === 0) return;

    setMarkingAll(true);
    try {
      const CHUNK = 450;
      for (let i = 0; i < unread.length; i += CHUNK) {
        const batch = writeBatch(db);
        unread.slice(i, i + CHUNK).forEach((n) => {
          batch.update(doc(db, 'notifications', n.id), {
            readBy: arrayUnion(user.uid),
          });
        });
        await batch.commit();
      }

      if (mountedRef.current) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.readBy?.includes(user.uid)
              ? n
              : { ...n, readBy: [...(n.readBy || []), user.uid] },
          ),
        );
      }
    } catch (err) {
      console.error('Mark all failed:', err);
      if (mountedRef.current) setError('Failed to mark all as read.');
    } finally {
      if (mountedRef.current) setMarkingAll(false);
    }
  }, [user?.uid, notifications]);

  /* ---------------- Mark selected as read ---------------- */
  const markSelectedAsRead = useCallback(async () => {
    if (!user?.uid || selectedIds.size === 0) return;
    const ids = [...selectedIds];

    // Optimistic
    setNotifications((prev) =>
      prev.map((n) =>
        ids.includes(n.id) && !n.readBy?.includes(user.uid)
          ? { ...n, readBy: [...(n.readBy || []), user.uid] }
          : n,
      ),
    );

    try {
      const CHUNK = 450;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = writeBatch(db);
        ids.slice(i, i + CHUNK).forEach((id) => {
          batch.update(doc(db, 'notifications', id), {
            readBy: arrayUnion(user.uid),
          });
        });
        await batch.commit();
      }
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Mark selected failed:', err);
      if (mountedRef.current) setError('Failed to mark some as read.');
    }
  }, [user?.uid, selectedIds]);

  /* ---------------- Bulk delete with undo ---------------- */
  const deleteSelected = useCallback(async () => {
    if (selectedIds.size === 0 || deleting) return;
    setDeleting(true);
    const ids = [...selectedIds];
    const removed = notifications.filter((n) => selectedIds.has(n.id));

    setNotifications((prev) => prev.filter((n) => !selectedIds.has(n.id)));

    try {
      const CHUNK = 450;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        const batch = writeBatch(db);
        slice.forEach((id) => batch.delete(doc(db, 'notifications', id)));
        await batch.commit();
      }
      if (mountedRef.current) {
        setSelectedIds(new Set());
        setSelectionMode(false);
        setDeleteOpen(false);
        setUndoData({ ids, count: removed.length, removed });
      }
    } catch (err) {
      console.error('Bulk delete failed:', err);
      if (mountedRef.current) {
        // Roll back optimistic removal (restore original order)
        setNotifications((prev) => {
          const byId = new Map(prev.map((n) => [n.id, n]));
          removed.forEach((r) => byId.set(r.id, r));
          return [...byId.values()].sort(
            (a, b) =>
              (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0),
          );
        });
        setDeleteOpen(false);
        setError(
          err?.code === 'permission-denied'
            ? "You don't have permission to delete some of these."
            : 'Failed to delete some notifications.',
        );
      }
    } finally {
      if (mountedRef.current) setDeleting(false);
    }
  }, [selectedIds, deleting, notifications]);

  /* ---------------- Undo delete ---------------- */
  /**
   * Restore previously-deleted notifications.
   * IMPORTANT: each `setDoc` is allowed to reject — any failure
   * bubbles to the outer catch so we can roll the UI back and tell
   * the user. (Previously each write was individually swallowed,
   * which meant a failed undo silently showed the rows as restored
   * while nothing was actually written to Firestore.)
   */
  const handleUndo = useCallback(async () => {
    if (!undoData) return;
    const { removed } = undoData;
    setUndoData(null);

    // Optimistic restore
    setNotifications((prev) => {
      const byId = new Map(prev.map((n) => [n.id, n]));
      removed.forEach((r) => byId.set(r.id, r));
      return [...byId.values()].sort(
        (a, b) =>
          (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0),
      );
    });

    try {
      // `merge: true` protects against clobbering concurrent edits
      // to a notification that was modified between delete and undo.
      await Promise.all(
        removed.map((n) =>
          setDoc(doc(db, 'notifications', n.id), n, { merge: true }),
        ),
      );
    } catch (err) {
      console.error('Undo failed:', err);
      if (mountedRef.current) {
        // Rollback: remove the optimistic restore
        setNotifications((prev) =>
          prev.filter((n) => !removed.some((r) => r.id === n.id)),
        );
        setError('Undo failed. The notifications were deleted.');
      }
    }
  }, [undoData]);

  /* ---------------- Selection helpers ---------------- */
  const toggleSelectionMode = () => {
    setSelectionMode((s) => {
      if (s) setSelectedIds(new Set());
      return !s;
    });
    // Reset keyboard focus anchor whenever entering/leaving selection mode
    focusedIndexRef.current = -1;
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(displayed.map((n) => n.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectUnread = () => {
    if (!user?.uid) return;
    const unreadIds = displayed
      .filter((n) => !n.readBy?.includes(user.uid))
      .map((n) => n.id);
    setSelectedIds(new Set(unreadIds));
  };

  const allSelected =
    displayed.length > 0 && selectedIds.size === displayed.length;

  /* ---------------- Keyboard shortcuts ---------------- */
  const focusedIndexRef = useRef(-1);

  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      if (e.key === 'Escape' && selectionMode) {
        setSelectionMode(false);
        setSelectedIds(new Set());
        return;
      }

      if (!selectionMode) return;

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        focusedIndexRef.current = Math.min(
          displayed.length - 1,
          focusedIndexRef.current + 1,
        );
        const node = listRef.current?.querySelector(
          `[data-idx="${focusedIndexRef.current}"]`,
        );
        node?.scrollIntoView({ block: 'nearest' });
        node?.focus();
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        focusedIndexRef.current = Math.max(0, focusedIndexRef.current - 1);
        const node = listRef.current?.querySelector(
          `[data-idx="${focusedIndexRef.current}"]`,
        );
        node?.scrollIntoView({ block: 'nearest' });
        node?.focus();
      } else if (e.key === 'x') {
        e.preventDefault();
        const node = listRef.current?.querySelector(
          `[data-idx="${focusedIndexRef.current}"]`,
        );
        const id = node?.getAttribute('data-id');
        if (id) toggleSelect(id);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectionMode, displayed]);

  /* ---------------- Click handler ---------------- */
  const handleClick = useCallback(
    async (n) => {
      if (selectionMode) {
        toggleSelect(n.id);
        return;
      }
      await markAsRead(n.id);
      if (n.link) {
        if (/^https?:\/\//i.test(n.link)) {
          window.open(n.link, '_blank', 'noopener,noreferrer');
        } else {
          navigate(n.link);
        }
      }
    },
    // eslint-disable-next-line
    [markAsRead, navigate, selectionMode],
  );

  /* ---------------- Derived ---------------- */
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.readBy?.includes(user?.uid)).length,
    [notifications, user?.uid],
  );

  /**
   * Count of unread items **visible on the current tab**.
   * Used so "Select unread" only advertises items it can actually
   * select on the active tab (e.g. on the read tab, this is 0).
   */
  const visibleUnreadCount = useMemo(
    () =>
      user?.uid
        ? displayed.filter((n) => !n.readBy?.includes(user.uid)).length
        : 0,
    [displayed, user?.uid],
  );

  const tabs = useMemo(
    () => [
      { id: 'all', label: 'All', count: notifications.length },
      { id: 'unread', label: 'Unread', count: unreadCount },
      {
        id: 'read',
        label: 'Read',
        count: notifications.length - unreadCount,
      },
    ],
    [notifications, unreadCount],
  );

  /* ---------------- Loading ---------------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <i
              className="fas fa-bell text-3xl text-orange"
              aria-hidden="true"
            />
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
              Notifications
            </h1>
          </div>
          <NotificationsSkeleton />
        </div>
      </div>
    );
  }

  /* ================================================================== */
  /* Render                                                              */
  /* ================================================================== */

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-28 pb-16">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <i
              className="fas fa-bell text-3xl text-orange"
              aria-hidden="true"
            />
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span
                aria-live="polite"
                className="bg-orange text-white text-xs px-2 py-1 rounded-full"
              >
                {unreadCount} unread
              </span>
            )}
          </motion.div>

          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/settings?tab=notifications"
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-orange flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 hover:border-orange transition"
              title="Notification preferences"
            >
              <i className="fas fa-cog" aria-hidden="true" />
              Preferences
            </Link>

            {notifications.length > 0 && !selectionMode && (
              <button
                type="button"
                onClick={toggleSelectionMode}
                className="text-sm text-gray-600 dark:text-gray-300 hover:text-orange flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 hover:border-orange transition"
              >
                <i className="fas fa-check-square" aria-hidden="true" />
                Select
              </button>
            )}

            {!selectionMode && unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                disabled={markingAll}
                className="text-sm text-orange hover:underline disabled:opacity-50 flex items-center gap-1"
              >
                {markingAll ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      aria-hidden="true"
                    />
                    Marking…
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-double" aria-hidden="true" />
                    Mark all as read
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              aria-pressed={tab === t.id}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${
                tab === t.id
                  ? 'bg-orange text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {t.label}
              {t.count > 0 && (
                <span
                  className={`text-[10px] px-1.5 rounded-full ${
                    tab === t.id ? 'bg-white/25' : 'bg-gray-100 dark:bg-gray-700'
                  }`}
                >
                  {t.count}
                </span>
              )}
            </button>
          ))}

          <span className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

          <button
            type="button"
            onClick={() => setGroupByDate((g) => !g)}
            aria-pressed={groupByDate}
            title={groupByDate ? 'Ungroup' : 'Group by date'}
            className={`text-xs px-3 py-1.5 rounded-full transition flex items-center gap-1.5 ${
              groupByDate
                ? 'bg-orange text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <i className="fas fa-calendar-day" aria-hidden="true" />
            <span className="hidden sm:inline">By date</span>
          </button>
        </div>

        {/* Selection toolbar */}
        <AnimatePresence>
          {selectionMode && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-orange/30 flex flex-wrap items-center gap-2"
            >
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                {selectedIds.size} selected
              </span>

              <span className="w-px h-4 bg-gray-200 dark:bg-gray-700" />

              <button
                type="button"
                onClick={allSelected ? clearSelection : selectAll}
                className="text-xs text-orange hover:underline"
              >
                {allSelected
                  ? 'Clear all'
                  : `Select all (${displayed.length})`}
              </button>

              {visibleUnreadCount > 0 && (
                <button
                  type="button"
                  onClick={selectUnread}
                  className="text-xs text-orange hover:underline"
                >
                  Select unread ({visibleUnreadCount})
                </button>
              )}

              <div className="flex-1" />

              <button
                type="button"
                onClick={markSelectedAsRead}
                disabled={selectedIds.size === 0}
                className="text-xs bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50 flex items-center gap-1"
              >
                <i className="fas fa-check" aria-hidden="true" />
                Mark read
              </button>

              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                disabled={selectedIds.size === 0}
                className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50 flex items-center gap-1"
              >
                <i className="fas fa-trash-alt" aria-hidden="true" />
                Delete selected
              </button>

              <button
                type="button"
                onClick={toggleSelectionMode}
                className="text-xs text-gray-600 dark:text-gray-300 hover:text-orange"
              >
                Cancel
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              role="alert"
              className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 rounded-lg text-sm flex items-start justify-between gap-2"
            >
              <span className="flex items-start gap-2">
                <i
                  className="fas fa-exclamation-circle mt-0.5"
                  aria-hidden="true"
                />
                {error}
              </span>
              <button
                type="button"
                onClick={() => setError('')}
                aria-label="Dismiss error"
                className="text-red-600 dark:text-red-300 hover:text-red-800"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty state */}
        {displayed.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl shadow-sm">
            <i
              className="fas fa-bell-slash text-5xl text-gray-400 mb-4"
              aria-hidden="true"
            />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
              {tab === 'unread'
                ? "You're all caught up!"
                : tab === 'read'
                ? 'No read notifications yet'
                : 'No notifications'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
              {tab === 'unread'
                ? 'No unread notifications.'
                : tab === 'read'
                ? 'Notifications you read will appear here.'
                : "We'll let you know when something happens."}
            </p>
          </div>
        ) : groupByDate && grouped ? (
          <div className="space-y-6">
            {grouped.map((group) => (
              <div key={group.label}>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                    {group.label}
                  </h2>
                  <span className="text-xs text-gray-500">
                    {group.items.length}
                  </span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className="space-y-3">
                  {group.items.map((notif) => (
                    <NotificationRow
                      key={notif.id}
                      notif={notif}
                      user={user}
                      selectionMode={selectionMode}
                      isSelected={selectedIds.has(notif.id)}
                      onClick={handleClick}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div ref={listRef} className="space-y-3">
            {displayed.map((notif, idx) => (
              <NotificationRow
                key={notif.id}
                notif={notif}
                idx={idx}
                user={user}
                selectionMode={selectionMode}
                isSelected={selectedIds.has(notif.id)}
                onClick={handleClick}
              />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && !selectionMode && tab === 'all' && (
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="px-5 py-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loadingMore ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  Loading…
                </>
              ) : (
                <>
                  <i className="fas fa-arrow-down" aria-hidden="true" />
                  Load more
                </>
              )}
            </button>
          </div>
        )}

        {!hasMore && notifications.length > PAGE_SIZE && !selectionMode && (
          <p className="text-center text-xs text-gray-400 mt-6">
            You've reached the end.
          </p>
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteOpen}
        title={`Delete ${selectedIds.size} notification${
          selectedIds.size === 1 ? '' : 's'
        }?`}
        message="This will permanently remove the selected notifications. Other users are not affected."
        confirmLabel={`Delete ${selectedIds.size}`}
        busy={deleting}
        onConfirm={deleteSelected}
        onCancel={() => setDeleteOpen(false)}
      />

      {/* Undo toast */}
      <AnimatePresence>
        {undoData && (
          <UndoToast
            key="undo"
            count={undoData.count}
            onUndo={handleUndo}
            onClose={() => setUndoData(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ================================================================== */
/* Notification Row                                                    */
/* ================================================================== */

const NotificationRow = ({
  notif,
  idx = 0,
  user,
  selectionMode,
  isSelected,
  onClick,
}) => {
  const isRead = notif.readBy?.includes(user?.uid);
  const style = getTypeStyle(notif.type);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ delay: Math.min(idx * 0.03, 0.3) }}
    >
      <div
        role={selectionMode ? 'checkbox' : 'button'}
        aria-checked={selectionMode ? isSelected : undefined}
        tabIndex={0}
        data-idx={idx}
        data-id={notif.id}
        onClick={() => onClick(notif)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(notif);
          }
        }}
        aria-label={
          selectionMode
            ? `${isSelected ? 'Deselect' : 'Select'} ${notif.title}`
            : isRead
            ? notif.title
            : `Unread: ${notif.title}. Click to mark as read.`
        }
        className={`w-full text-left bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition cursor-pointer p-4 border-l-4 flex items-start gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange ${
          isSelected
            ? 'border-orange bg-orange/10 dark:bg-orange/15 ring-2 ring-orange/40'
            : isRead
            ? 'border-gray-300 dark:border-gray-600'
            : 'border-orange bg-orange/5 dark:bg-orange/10'
        }`}
      >
        {selectionMode && (
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition ${
              isSelected
                ? 'bg-orange border-orange text-white'
                : 'border-gray-300 dark:border-gray-500'
            }`}
            aria-hidden="true"
          >
            {isSelected && <i className="fas fa-check text-[10px]" />}
          </div>
        )}

        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${style.bg}`}
        >
          <i
            className={`fas ${style.icon} ${style.color}`}
            aria-hidden="true"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4
              className={`font-semibold text-gray-800 dark:text-white ${
                isRead && !selectionMode ? '' : 'text-orange'
              }`}
            >
              {notif.title}
            </h4>
            {!isRead && !selectionMode && (
              <span
                className="w-2 h-2 bg-orange rounded-full mt-2 flex-shrink-0"
                aria-hidden="true"
              />
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">
            {notif.body}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <p className="text-xs text-gray-400">
              {formatRelative(notif.createdAt)}
            </p>
            {notif.link && !selectionMode && (
              <>
                <span className="text-xs text-gray-300">·</span>
                <span className="text-xs text-orange flex items-center gap-1">
                  View
                  <i
                    className="fas fa-arrow-right text-[8px]"
                    aria-hidden="true"
                  />
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Notifications;